import { forwardRef, type ElementRef, type ReactNode, type ReactElement } from 'react';
import {
  Popover as HUIPopover,
  PopoverButton as HUIPopoverButton,
  PopoverPanel as HUIPopoverPanel,
  type PopoverProps as HUIPopoverProps,
  type PopoverButtonProps as HUIPopoverButtonProps,
  type PopoverPanelProps as HUIPopoverPanelProps,
} from '@headlessui/react';
import { cn } from '../../../lib/cn.js';

/**
 * Glass-surface Popover built on Headless UI v2.
 * - Focus management, ESC-to-close, and outside-click are handled by Headless UI.
 * - Positioned via the `anchor` prop on PopoverContent (e.g. `anchor="bottom end"`).
 */
export function Popover(props: HUIPopoverProps) {
  return <HUIPopover {...props} />;
}

// `asChild` is intentionally NOT exposed: Headless UI v2 uses `as={Fragment}`
// for the shadcn "render as child" convention. Consumers needing a non-button
// trigger should pass `as={Fragment}` and wrap their own element.
type PopoverTriggerProps = Omit<HUIPopoverButtonProps, 'as'>;

/** Invisible wrapper — renders a `button` by default; pass `as={Fragment}` to use an existing child element. */
export const PopoverTrigger = forwardRef<ElementRef<typeof HUIPopoverButton>, PopoverTriggerProps>(
  function PopoverTrigger({ className, ...rest }, ref) {
    const resolvedClass = typeof className === 'string' ? className : undefined;
    return (
      <HUIPopoverButton
        ref={ref}
        className={cn(
          'outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400 rounded-full',
          resolvedClass,
        )}
        {...rest}
      />
    );
  },
);

type PanelRenderProp = (bag: { open: boolean; close: () => void }) => ReactElement;

type PopoverContentProps = Omit<HUIPopoverPanelProps, 'anchor' | 'transition'> & {
  /**
   * Anchor config forwarded to Headless UI. Defaults to `bottom end` with an 8px gap.
   * Examples: `"bottom end"`, `"top start"`, or an object like `{ to: 'bottom end', gap: 8 }`.
   */
  anchor?: HUIPopoverPanelProps['anchor'];
  className?: string | undefined;
  children?: ReactNode | PanelRenderProp;
};

/** Glass surface panel. Uses HeadlessUI's `transition` so Motion is not required here. */
export const PopoverContent = forwardRef<ElementRef<typeof HUIPopoverPanel>, PopoverContentProps>(
  function PopoverContent(
    { className, anchor = { to: 'bottom end', gap: 8 }, children, ...rest },
    ref,
  ) {
    return (
      <HUIPopoverPanel
        ref={ref}
        anchor={anchor}
        transition
        className={cn(
          'z-50 w-64 origin-top rounded-lg border border-white/10 bg-surface-elevated/95 p-1 text-primary shadow-xl backdrop-blur-md',
          // Headless UI transition data attrs
          'transition duration-150 ease-out',
          'data-[closed]:scale-95 data-[closed]:opacity-0',
          'focus:outline-none',
          className,
        )}
        {...rest}
      >
        {children as ReactNode}
      </HUIPopoverPanel>
    );
  },
);

interface SectionProps {
  className?: string;
  children: ReactNode;
}

export function PopoverHeader({ className, children }: SectionProps) {
  return <div className={cn('border-b border-white/10 px-3 py-3', className)}>{children}</div>;
}

export function PopoverBody({ className, children }: SectionProps) {
  return <div className={cn('py-1', className)}>{children}</div>;
}

export function PopoverFooter({ className, children }: SectionProps) {
  return <div className={cn('border-t border-white/10 px-3 py-2', className)}>{children}</div>;
}
